import { memo } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import clsx from 'clsx';

import { Typography } from '@mui/material';
import { useStyles } from './styles';

// Modify the clipboard behavior to allow only plain text
const Clipboard = Quill.import('modules/clipboard');
const Delta = Quill.import('delta');
const Parchment = Quill.import('parchment');

class PlainClipboard extends Clipboard {
  onPaste(e: any) {
    // Prevent default behavior
    e.preventDefault();

    // Get html data from clipboard. A copy from a plain-text source (a text editor, a
    // terminal) has no HTML, only text/plain.
    const html = e.clipboardData.getData('text/html');
    const plainText = e.clipboardData.getData('text/plain');
    if (!html && !plainText) return;
    // Create a DOM parser to manipulate the pasted HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Function to recursively remove all inline styles from elements
    const removeInlineStyles = (element: Element) => {
      if (element.nodeType === Node.ELEMENT_NODE) {
        element.removeAttribute('style');
        // Recursively apply the same function to child elements
        // eslint-disable-next-line no-restricted-syntax
        for (const child of Array.from(new Set(element.children))) {
          removeInlineStyles(child);
        }
      }
    };

    // Apply the style removal to the body of the parsed document
    removeInlineStyles(doc.body);

    // Get the cleaned HTML string
    const sanitizedData = doc.body.innerHTML;

    // Replace the selection, as a normal paste does, and mark the change as the user's.
    // dangerouslyPasteHTML would report it as 'api', the same source Quill uses when it
    // rewrites HTML it has just loaded, so listeners could not tell a paste from that.
    const range = this.quill.getSelection(true);
    const paste = html ? this.convert(sanitizedData) : this.convertPlainText(plainText, range.index);
    this.quill.updateContents(new Delta().retain(range.index).delete(range.length).concat(paste), 'user');
    this.quill.setSelection(range.index + paste.length(), 'silent');
  }

  // In Quill a line's block format (list, header, code block, ...) lives on the newline that
  // ends it. A bare pasted newline would end the current line as a plain paragraph, so the
  // text before it would lose its list bullet or code block. Give each pasted newline the
  // current line's block format instead, as Quill's own paste does inside a code block.
  convertPlainText(text: string, index: number) {
    const formats = this.quill.getFormat(index);
    const lineFormats = Object.keys(formats)
      .filter((name) => Parchment.query(name, Parchment.Scope.BLOCK))
      .reduce((picked: Record<string, unknown>, name) => ({ ...picked, [name]: formats[name] }), {});
    return text.replace(/\r\n?/g, '\n').split('\n').reduce(
      (delta: any, line: string, i: number) => (i ? delta.insert('\n', lineFormats) : delta).insert(line),
      new Delta()
    );
  }
}

// Register the new clipboard behavior
Quill.register('modules/clipboard', PlainClipboard);

interface IEditorProps {
  isRequired?: boolean,
  onChange?: (res: string, source: 'api' | 'user' | 'silent') => void,
  onBlur?: (res: string) => void,
  classNameForEditor?: string,
  placeholder?: string,
  modules?: {
    [key: string]: any | { [key: string]: any }
  },
  fieldCommentTop?: string,
  fieldCommentBottom?: string,
  error?: string | null,
  value?: string | null,
}

const defaultModules:any = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    ['blockquote'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }, { align: [] }],
    ['link', 'image'],
    ['clean'],
  ],
  clipboard: {
    matchVisual: false,
  }
};

const Editor = ({ onChange, classNameForEditor, placeholder, modules, onBlur, fieldCommentTop, fieldCommentBottom, error, value, isRequired }:IEditorProps) => {
  const classes = useStyles();

  const handleChange = (result: string, source: 'api' | 'user' | 'silent') => {
    if (onChange) {
      onChange(result, source);
    }
  };

  const handleBlur = (result: string) => {
    if (onBlur) {
      onBlur(result);
    }
  };

  return (
     <div className={classes.wrapper_editor}>
        {fieldCommentTop && (
        <Typography
           className={classes.fieldCommentTop}
           color="textSecondary"
           variant="body2"
        >
           {!isRequired ? fieldCommentTop : (
              <>
                 {fieldCommentTop}
                 <span style={{ color: 'red' }}>*</span>
              </>
           )}
        </Typography>
        )}
        <div data-cy="editor">
           <ReactQuill
              value={value || ''}
              onBlur={(a, b, c) => {
                handleBlur(c.getHTML());
              }}
              onChange={(a, b, source) => {
                handleChange(a, source);
              }}
              theme="snow"
              modules={modules || defaultModules}
              placeholder={placeholder}
              className={(clsx(classes.editor, classNameForEditor, {
                [classes.error]: !!error
              }))}
           />
        </div>
        {error && (
        <Typography
           color="error"
           variant="body2"
        >
           {error}
        </Typography>
        )}
        {fieldCommentBottom && (
        <Typography
           className={classes.fieldCommentBottom}
           color="textSecondary"
           variant="body2"
           fontWeight={600}
        >
           {fieldCommentBottom}
        </Typography>
        )}
     </div>
  );
};

export default memo(Editor);
